/**
 * Audit-report claim handshake — dashboard side of the 2026-08-19 report
 * teardown ("Get the fixes for these 71 routed to a bare sign-up").
 *
 * The outreach audit report's sign-up CTAs carry `share=<shareId>`, built by
 * signupCtaUrl in packages/server/src/services/reportCtaExperiment.ts.
 * /sign-up redirects to /sign-in preserving the query string, but those params
 * are dropped on the post-auth navigate() and lost entirely on the Google
 * OAuth full-page redirect. So we STASH the shareId the moment the sign-in
 * page mounts and CONSUME it after bootstrap creates the account
 * (useSiteAuditClaim -> POST /api/site-audits/:shareId/claim, which creates a
 * project holding the report's findings as issues).
 *
 * Deliberately a sibling of lib/freeScanClaim.ts with its OWN storage keys: a
 * report share must never be mistaken for a free-scan review token, and each
 * module stays the single source of truth for its stash.
 */
import { log } from '@/lib/logger';

const SHARE_KEY = 'oqa_site_audit_claim_share';
const STAMP_KEY = 'oqa_site_audit_claim_ts';

/**
 * Same bound and rationale as FREE_SCAN_CLAIM_TTL_MS: in a shared browser a
 * stash somebody abandoned must not silently attach to a DIFFERENT person
 * signing in later. The happy path consumes within seconds.
 */
export const SITE_AUDIT_CLAIM_TTL_MS = 60 * 60 * 1000; // 1 hour

/** shareIds are 12 hex chars today; cap defensively against junk URLs. */
const SHARE_MAX = 200;

export interface SiteAuditClaim {
  shareId: string;
}

function isFresh(now: number): boolean {
  try {
    const raw = sessionStorage.getItem(STAMP_KEY);
    if (!raw) return false;
    const stamp = Number(raw);
    return Number.isFinite(stamp) && now - stamp < SITE_AUDIT_CLAIM_TTL_MS;
  } catch {
    return false;
  }
}

/**
 * Stash the shareId from the sign-in URL so it survives Clerk's OAuth
 * full-page redirect and the post-auth navigate(). Called at /sign-in mount.
 * No-op (and leaves any existing stash untouched) when no share is present.
 */
export function captureSiteAuditClaimParams(
  search: string,
  now: number = Date.now(),
): { shareId: string | null } {
  let shareId: string | null = null;
  try {
    const raw = new URLSearchParams(search).get('share');
    shareId = raw && raw.length > 0 && raw.length <= SHARE_MAX ? raw : null;
  } catch {
    // A malformed search string must not crash the sign-in page.
    return { shareId: null };
  }

  if (!shareId) return { shareId: null };

  try {
    sessionStorage.setItem(SHARE_KEY, shareId);
    sessionStorage.setItem(STAMP_KEY, String(now));
    log.info('[siteAuditClaim] captured share param', { shareId });
  } catch {
    // sessionStorage throws in private mode — the claim simply will not carry.
  }
  return { shareId };
}

/** Read the stashed shareId. Clears + returns null when stale. */
export function readSiteAuditClaim(now: number = Date.now()): SiteAuditClaim | null {
  let shareId: string | null = null;
  try {
    shareId = sessionStorage.getItem(SHARE_KEY);
  } catch {
    return null;
  }
  if (!shareId) return null;
  if (!isFresh(now)) {
    clearSiteAuditClaim();
    return null;
  }
  return { shareId };
}

export function clearSiteAuditClaim(): void {
  try {
    sessionStorage.removeItem(SHARE_KEY);
    sessionStorage.removeItem(STAMP_KEY);
  } catch {
    // ignore — nothing to clear if storage is unavailable
  }
}
